/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CapacityInsightsPage.tsx
 *     Feature         :   216-ui-cleanup (T040 / US7 — the pattern-setter Insights pilot)
 *     Description     :   Capacity Insights — the true capacity dashboard that replaces
 *                         the Capacity Portfolio screen: fleet KPIs, 60d + intraday
 *                         utilization, throttling timeline, right-sizing verdicts with
 *                         real cost deltas, efficiency ranking, and weekday×hour usage
 *                         fingerprints. Everything renders from collected data (FR-018);
 *                         missing signals say "not collected", never 0.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Gauge, TrendingDown, TrendingUp, CheckCircle2, PauseCircle, Info, AlertTriangle } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, BarChart, Bar,
} from 'recharts';
import { apiClient } from '../../services/apiClient';
import { usePageData } from '../../hooks/usePageData';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useCanViewCost } from '../../hooks/useCanViewCost';
import { UnmeasuredSizeTag } from '@/utils/unmeasuredSize';
import {
  PageLayout,
  Card,
  DataTable,
  Badge,
  StatCard,
  ErrorState,
  LoadingState,
  NotCollected,
  AppTooltip,
  Extended,
  QuickGlance,
  chartTooltipStyle,
  Select,
  CostSourceBadge,
  formatUsd,
} from '../../components/ui';
import type { DataTableColumn, BadgeVariant } from '../../components/ui';
import { formatEastern } from '../../utils/dateFormatter';

/** Categorical palette for per-capacity multi-series charts — same pattern as
 * components/Dashboard/panels/CuFleetPanel.tsx's SMA_LINE_COLORS (recharts
 * series need more distinct hues than the token set provides). */
const SERIES_COLORS = ['#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#06b6d4', '#ec4899', '#eab308', '#3b82f6', '#84cc16', '#f97316'];
const seriesColor = (i: number): string => SERIES_COLORS[i % SERIES_COLORS.length];

// ============================================================================
// Payload types (mirror capacityInsightsRepository)
// ============================================================================

type Verdict = 'downsize' | 'upgrade' | 'right-sized' | 'not-collected';

interface RightSizingRow {
  capacityId: string;
  name: string;
  sku: string | null;
  subscriptionName: string | null;
  isPaused: boolean;
  peakPct: number | null;
  p95Pct: number | null;
  avgPct: number | null;
  verdict: Verdict;
  suggestedSku: string | null;
  monthlyDeltaUsd: number | null;
  rateNote: string | null;
  currentMonthlyUsd: number | null;
}

interface EfficiencyRow {
  capacityId: string;
  name: string;
  sku: string | null;
  subscriptionName: string | null;
  avgPct: number | null;
  currentMonthlyUsd: number | null;
  pricePerHour: number | null;
  score: number | null;
  rank: number | null;
}

interface Above80WindowCounts {
  now: number;
  last1h: number;
  last24h: number;
  last7d: number;
  last30d: number;
}

interface HitListRow {
  capacityId: string;
  name: string;
  sku: string | null;
  subscriptionName: string | null;
  hitsNow: boolean;
  hits1h: number;
  hits24h: number;
  hits7d: number;
  hits30d: number;
}

interface PerCapacityTrendRow {
  capacityId: string;
  name: string;
  series: Array<{ date: string; avgPct: number; maxPct: number }>;
}

interface FingerprintRow {
  capacityId: string;
  name: string;
  grid: Array<Array<number | null>>;
  stateEvents: Array<{ at: string; eventType: string }>;
}

interface InsightsPayload {
  kpis: {
    fleetAvg24hPct: number;
    fleetMax24hPct: number;
    capacitiesAbove80: number;
    pausedCount: number;
    activeCapacities: number;
  };
  trend60d: Array<{ date: string; avgPct: number; maxPct: number }>;
  perCapacityTrend60d: PerCapacityTrendRow[];
  intraday24h: Array<{ hourUtc: string; avgPct: number; maxPct: number }>;
  throttlingTimeline: Array<{ capacityId: string; capacityName: string; hourUtc: string; readingsAbove80: number }>;
  above80Windows: Above80WindowCounts;
  hitList: HitListRow[];
  rightSizing: RightSizingRow[];
  efficiencyRanking: EfficiencyRow[];
  fingerprints: FingerprintRow[];
  distribution: Array<{ capacityId: string; name: string; subscriptionName: string | null; workspaceCount: number; lakehouseCount: number; warehouseCount: number; storageGb: number; sizeNotMeasuredCount?: number }>;
  ratesAsOf: string | null;
}

const VERDICT_BADGE: Record<Verdict, { variant: BadgeVariant; label: string; icon: React.ReactNode }> = {
  downsize: { variant: 'info', label: 'Downsize', icon: <TrendingDown className="h-3 w-3" /> },
  upgrade: { variant: 'error', label: 'Upgrade', icon: <TrendingUp className="h-3 w-3" /> },
  'right-sized': { variant: 'success', label: 'Right-sized', icon: <CheckCircle2 className="h-3 w-3" /> },
  'not-collected': { variant: 'secondary', label: 'Not collected', icon: <PauseCircle className="h-3 w-3" /> },
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Token-safe heat color for a CU% cell. Six perceptually-distinct buckets
 * (accent → warning → danger, ramping alpha within each) — a plain linear
 * alpha-of-pct scale left most real-world cells (typically 5–40% CU) looking
 * nearly identical, since the alpha differences in that range were tiny.
 */
const HEAT_BUCKETS: Array<{ max: number; color: string; alphaPct: number; label: string }> = [
  { max: 5, color: 'var(--color-accent)', alphaPct: 12, label: '0–5%' },
  { max: 20, color: 'var(--color-accent)', alphaPct: 32, label: '5–20%' },
  { max: 40, color: 'var(--color-accent)', alphaPct: 65, label: '20–40%' },
  { max: 60, color: 'var(--color-status-warning)', alphaPct: 55, label: '40–60%' },
  { max: 80, color: 'var(--color-status-danger)', alphaPct: 45, label: '60–80%' },
  { max: Infinity, color: 'var(--color-status-danger)', alphaPct: 85, label: '80%+' },
];

function heatStyle(pct: number | null): React.CSSProperties {
  if (pct == null) return { backgroundColor: 'var(--color-bg-subtle)' };
  const bucket = HEAT_BUCKETS.find((b) => pct < b.max) ?? HEAT_BUCKETS[HEAT_BUCKETS.length - 1];
  return { backgroundColor: `color-mix(in srgb, ${bucket.color} ${bucket.alphaPct}%, transparent)` };
}

const HeatLegend: React.FC = () => (
  <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-[var(--color-text-secondary)]">
    <span className="font-medium text-[var(--color-text-primary)]">Mean CU% legend:</span>
    {HEAT_BUCKETS.map((b) => (
      <div key={b.label} className="flex items-center gap-1">
        <div className="h-3 w-3 rounded-[2px]" style={{ backgroundColor: `color-mix(in srgb, ${b.color} ${b.alphaPct}%, transparent)` }} />
        <span>{b.label}</span>
      </div>
    ))}
    <div className="flex items-center gap-1">
      <div className="h-3 w-3 rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-subtle)' }} />
      <span>no readings</span>
    </div>
  </div>
);

const pctOrDash = (v: number | null): string => (v == null ? '—' : `${v}%`);

// ============================================================================
// Panels
// ============================================================================

const FingerprintCard: React.FC<{ fp: FingerprintRow }> = ({ fp }) => (
  <Card>
    <div className="mb-2 flex items-center justify-between">
      <p className="text-sm font-medium text-[var(--color-text-primary)]">{fp.name}</p>
      {fp.stateEvents.length > 0 && (
        <AppTooltip title={`${fp.stateEvents.length} pause/resume state events in the window`}>
          <span>
            <Badge variant="warning" isOutlined size="sm">{fp.stateEvents.length} state events</Badge>
          </span>
        </AppTooltip>
      )}
    </div>
    <div className="overflow-x-auto">
      <table className="border-separate" style={{ borderSpacing: 2 }}>
        <tbody>
          {fp.grid.map((row, wd) => (
            <tr key={wd}>
              <td className="pr-1 text-[10px] text-[var(--color-text-secondary)]">{WEEKDAYS[wd]}</td>
              {row.map((pct, hr) => (
                <td key={hr} title={`${WEEKDAYS[wd]} ${hr}:00 — ${pct == null ? 'no readings' : `${pct}% avg CU`}`}>
                  <div className="h-3 w-3 rounded-[2px]" style={heatStyle(pct)} />
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td />
            {Array.from({ length: 24 }, (_, hr) => (
              <td key={hr} className="text-center text-[8px] text-[var(--color-text-secondary)]">
                {hr % 6 === 0 ? hr : ''}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  </Card>
);

// ============================================================================
// Page
// ============================================================================

export const CapacityInsightsPage: React.FC = () => {
  const query = useQuery<InsightsPayload>({
    queryKey: ['capacity-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: InsightsPayload }>('/api/capacity-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });
  // Spec 226 — standard page-affordance bundle + silent 5-min auto-refresh
  const { data, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);
  useAutoRefresh(refresh, { lastFetchedAt });

  // Owner directive: cost / spend figures are Admin + Manager only.
  const canViewCost = useCanViewCost();

  // Per-capacity avg/peak trend charts — capacity filter (in/out toggle)
  const [hiddenCapacities, setHiddenCapacities] = useState<Set<string>>(new Set());
  const toggleCapacity = (id: string) =>
    setHiddenCapacities((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Usage fingerprint — single diagram, capacity picked from a selector
  const [selectedFingerprintId, setSelectedFingerprintId] = useState<string | null>(null);

  const perCapacityWide = useMemo(() => {
    const dateMap = new Map<string, Record<string, number | string>>();
    for (const t of data?.perCapacityTrend60d ?? []) {
      for (const pt of t.series) {
        let row = dateMap.get(pt.date);
        if (!row) { row = { date: pt.date }; dateMap.set(pt.date, row); }
        row[`avg__${t.capacityId}`] = pt.avgPct;
        row[`max__${t.capacityId}`] = pt.maxPct;
      }
    }
    return [...dateMap.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [data?.perCapacityTrend60d]);

  const throttlingByDay = useMemo(() => {
    const dayMap = new Map<string, Record<string, number | string>>();
    const capacities = new Map<string, string>();
    for (const t of data?.throttlingTimeline ?? []) {
      const day = t.hourUtc.slice(0, 10);
      let row = dayMap.get(day);
      if (!row) { row = { date: day }; dayMap.set(day, row); }
      row[t.capacityId] = (Number(row[t.capacityId]) || 0) + t.readingsAbove80;
      capacities.set(t.capacityId, t.capacityName);
    }
    return {
      days: [...dayMap.values()].sort((a, b) => String(a.date).localeCompare(String(b.date))),
      capacities: [...capacities.entries()],
    };
  }, [data?.throttlingTimeline]);

  const rightSizingColumns: DataTableColumn<RightSizingRow>[] = [
    {
      key: 'name',
      header: 'Capacity',
      width: '22%',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--color-text-primary)]">{r.name}</div>
          <div className="truncate text-xs text-[var(--color-text-secondary)]">
            {r.sku ?? '—'} · {r.subscriptionName ?? 'no subscription'}{r.isPaused ? ' · paused' : ''}
          </div>
        </div>
      ),
    },
    { key: 'peakPct', header: 'Peak 60d', width: '10%', align: 'right', render: (r) => pctOrDash(r.peakPct) },
    { key: 'p95Pct', header: 'P95', width: '10%', align: 'right', render: (r) => pctOrDash(r.p95Pct) },
    { key: 'avgPct', header: 'Avg', width: '10%', align: 'right', render: (r) => pctOrDash(r.avgPct) },
    {
      key: 'verdict',
      header: 'Verdict',
      width: '16%',
      render: (r) => {
        const v = VERDICT_BADGE[r.verdict];
        return (
          <AppTooltip title="Rules: peak < 40% → downsize · P95 > 80% sustained ≥7 days → upgrade · paused/no telemetry → not collected">
            <span>
              <Badge variant={v.variant} leftIcon={v.icon} isPill>{v.label}</Badge>
            </span>
          </AppTooltip>
        );
      },
    },
    {
      key: 'currentMonthlyUsd',
      header: 'Current cost (30d)',
      width: '16%',
      align: 'right',
      render: (r) => (r.currentMonthlyUsd == null
        ? <NotCollected reason="No Azure billing for this capacity in the last 30 days (dbo.azure_cost_daily)" />
        : <span className="font-medium text-[var(--color-text-primary)]">{formatUsd(r.currentMonthlyUsd)}</span>),
    },
    {
      key: 'monthlyDeltaUsd',
      header: 'Projected Δ (list price)',
      width: '18%',
      align: 'right',
      render: (r) => {
        if (r.verdict === 'right-sized' || r.verdict === 'not-collected') {
          return <span className="text-[var(--color-text-secondary)]">—</span>;
        }
        if (r.monthlyDeltaUsd == null) {
          return <NotCollected reason={r.rateNote ?? 'rate not collected'} />;
        }
        const label = r.verdict === 'downsize'
          ? `save ~$${r.monthlyDeltaUsd.toLocaleString()}/mo`
          : `+$${r.monthlyDeltaUsd.toLocaleString()}/mo`;
        return (
          <span className={r.verdict === 'downsize' ? 'font-medium text-[var(--color-status-success)]' : 'font-medium text-[var(--color-status-warning)]'}>
            {r.suggestedSku ? `${r.suggestedSku} · ` : ''}{label}
          </span>
        );
      },
    },
  ];

  const efficiencyColumns: DataTableColumn<EfficiencyRow>[] = [
    { key: 'rank', header: '#', width: '6%', align: 'right', render: (r) => r.rank ?? '—' },
    {
      key: 'name',
      header: 'Capacity',
      width: '30%',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--color-text-primary)]">{r.name}</div>
          <div className="truncate text-xs text-[var(--color-text-secondary)]">
            {r.sku ?? '—'} · {r.subscriptionName ?? 'no subscription'}
          </div>
        </div>
      ),
    },
    { key: 'avgPct', header: 'Avg CU', width: '12%', align: 'right', render: (r) => pctOrDash(r.avgPct) },
    {
      key: 'pricePerHour',
      header: '$/hr',
      width: '12%',
      align: 'right',
      render: (r) => (r.pricePerHour == null ? <NotCollected reason="No Azure billing for this capacity in the last 30 days (dbo.azure_cost_daily)" /> : `$${r.pricePerHour.toFixed(2)}`),
    },
    {
      key: 'score',
      header: 'Efficiency',
      width: '22%',
      render: (r) =>
        r.score == null ? (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ) : (
          <div className="flex items-center gap-2">
            <div className="h-2 w-24 overflow-hidden rounded bg-[var(--color-bg-subtle)]">
              <div className="h-full rounded bg-[var(--color-accent)]" style={{ width: `${r.score}%` }} />
            </div>
            <span className="text-xs text-[var(--color-text-secondary)]">{r.score}</span>
          </div>
        ),
    },
  ];

  const hitListColumns: DataTableColumn<HitListRow>[] = [
    {
      key: 'name',
      header: 'Capacity',
      width: '28%',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--color-text-primary)]">{r.name}</div>
          <div className="truncate text-xs text-[var(--color-text-secondary)]">
            {r.sku ?? '—'} · {r.subscriptionName ?? 'no subscription'}
          </div>
        </div>
      ),
    },
    {
      key: 'hitsNow',
      header: 'Now',
      width: '18%',
      render: (r) =>
        r.hitsNow ? (
          <Badge variant="error" leftIcon={<AlertTriangle className="h-3 w-3" />} isPill>Above 80%</Badge>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    { key: 'hits1h', header: 'Last hour', width: '13%', align: 'right', render: (r) => (r.hits1h > 0 ? r.hits1h : <span className="text-[var(--color-text-secondary)]">0</span>) },
    { key: 'hits24h', header: 'Last 24h', width: '13%', align: 'right', render: (r) => (r.hits24h > 0 ? r.hits24h : <span className="text-[var(--color-text-secondary)]">0</span>) },
    { key: 'hits7d', header: 'Last 7d', width: '13%', align: 'right', render: (r) => (r.hits7d > 0 ? r.hits7d : <span className="text-[var(--color-text-secondary)]">0</span>) },
    { key: 'hits30d', header: 'Last 30d', width: '15%', align: 'right', render: (r) => (r.hits30d > 0 ? <span className="font-medium">{r.hits30d}</span> : <span className="text-[var(--color-text-secondary)]">0</span>) },
  ];

  const effectiveFingerprintId = selectedFingerprintId ?? data?.fingerprints[0]?.capacityId ?? null;
  const selectedFingerprint = data?.fingerprints.find((f) => f.capacityId === effectiveFingerprintId) ?? null;

  return (
    <PageLayout
      title="Capacity Insights"
      description="What is the fleet doing, is anything saturated, and is every capacity the right size — from collected telemetry and list prices"
      icon={<Gauge className="h-6 w-6" />}
      helpId="insights.capacity"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
    >
      {error && !data ? (
        <ErrorState title="Failed to load capacity insights" message={(error as Error).message} onRetry={refresh} />
      ) : isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <>
          {/* KPI row */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className="relative h-full">
              <StatCard className="h-full" label="Fleet Avg (24h)" value={`${data.kpis.fleetAvg24hPct}%`} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Average CU utilization across all active capacities over the last 24h, from collected eventstream telemetry (capacity_telemetry_snapshots, the authoritative CU source). A low fleet average with a high peak means bursty, uneven load.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard
                className="h-full"
                label="Fleet Peak (24h)"
                value={`${data.kpis.fleetMax24hPct}%`}
                valueClassName={data.kpis.fleetMax24hPct > 80 ? 'text-[var(--color-status-danger)]' : undefined}
              />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Highest single CU reading across the fleet in the last 24h. Over 100% means a capacity was throttling/overloaded (turns red above 80%). Reconcile with the 'Capacities above 80%' panel below to see which ones.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Paused" value={data.kpis.pausedCount} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Capacities currently Suspended (paused) — they incur no compute cost but their workspaces are unavailable until resumed. From dbo.capacities.status.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Active Capacities" value={data.kpis.activeCapacities} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="All active (is_active) capacities in the fleet — the denominator for the utilization and right-sizing analysis below.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for trends, right-sizing, and fingerprints.
            </Card>
          </QuickGlance>

          <Extended>
          <div className="space-y-6">
            {/* Saturation — capacities above 80% CU, per-window + hit list */}
            <Card>
              <div className="mb-1 flex items-center gap-2">
                <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Capacities above 80% CU</h2>
                <AppTooltip title="Count of DISTINCT capacities with at least one CU reading over 80% in each window. 'Now' looks only at each capacity's single latest reading.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
              <p className="mb-4 text-xs text-[var(--color-text-secondary)]">How many capacities have crossed 80% utilization, and when — the hit list below ranks the worst offenders over the last 30 days.</p>
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                <StatCard label="Now" value={data.above80Windows.now} valueClassName={data.above80Windows.now > 0 ? 'text-[var(--color-status-danger)]' : undefined} />
                <StatCard label="Last hour" value={data.above80Windows.last1h} valueClassName={data.above80Windows.last1h > 0 ? 'text-[var(--color-status-warning)]' : undefined} />
                <StatCard label="Last 24h" value={data.above80Windows.last24h} valueClassName={data.above80Windows.last24h > 0 ? 'text-[var(--color-status-warning)]' : undefined} />
                <StatCard label="Last 7 days" value={data.above80Windows.last7d} />
                <StatCard label="Last 30 days" value={data.above80Windows.last30d} />
              </div>
              <DataTable
                columns={hitListColumns}
                rows={data.hitList}
                rowKey={(r) => r.capacityId}
                pageSize={10}
                emptyMessage="No collectable capacities"
              />
            </Card>

            {/* 60d trend + intraday */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <Card>
                <h2 className="mb-3 text-base font-semibold text-[var(--color-text-primary)]">Fleet utilization — 60 days</h2>
                {data.trend60d.length > 1 ? (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.trend60d}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} unit="%" width={40} />
                        <Tooltip {...chartTooltipStyle} />
                        <Legend />
                        <Line type="monotone" dataKey="avgPct" name="Avg CU%" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="maxPct" name="Peak CU%" stroke="var(--color-status-warning)" strokeWidth={1.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <NotCollected reason="Fewer than 2 days of telemetry collected in the 60-day window" />
                )}
              </Card>

              <Card>
                <h2 className="mb-3 text-base font-semibold text-[var(--color-text-primary)]">Intraday — last 24 hours</h2>
                {data.intraday24h.length > 1 ? (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.intraday24h.map((p) => ({ ...p, hour: new Date(p.hourUtc).getUTCHours() }))}>
                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} unit="%" width={40} />
                        <Tooltip {...chartTooltipStyle} />
                        <Legend />
                        <Line type="monotone" dataKey="avgPct" name="Avg CU%" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="maxPct" name="Peak CU%" stroke="var(--color-status-warning)" strokeWidth={1.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <NotCollected reason="No telemetry collected in the last 24 hours" />
                )}
              </Card>
            </div>

            {/* Per-capacity avg/peak breakdown — filterable */}
            {data.perCapacityTrend60d.length > 0 && (
              <Card>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Per-capacity breakdown — 60 days</h2>
                  <span className="text-xs text-[var(--color-text-secondary)]">Click a capacity to filter it in/out of both charts below</span>
                </div>
                <div className="mb-4 flex flex-wrap gap-2">
                  {data.perCapacityTrend60d.map((t, i) => {
                    const hidden = hiddenCapacities.has(t.capacityId);
                    return (
                      <button
                        key={t.capacityId}
                        type="button"
                        onClick={() => toggleCapacity(t.capacityId)}
                        className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-opacity"
                        style={{
                          borderColor: seriesColor(i),
                          opacity: hidden ? 0.35 : 1,
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: seriesColor(i) }} />
                        {t.name}
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">Avg CU% by capacity</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={perCapacityWide}>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} unit="%" width={40} />
                          <Tooltip {...chartTooltipStyle} />
                          {data.perCapacityTrend60d.map((t, i) => !hiddenCapacities.has(t.capacityId) && (
                            <Line key={t.capacityId} type="monotone" dataKey={`avg__${t.capacityId}`} name={t.name} stroke={seriesColor(i)} strokeWidth={1.5} dot={false} connectNulls />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">Peak CU% by capacity</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={perCapacityWide}>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} unit="%" width={40} />
                          <Tooltip {...chartTooltipStyle} />
                          {data.perCapacityTrend60d.map((t, i) => !hiddenCapacities.has(t.capacityId) && (
                            <Line key={t.capacityId} type="monotone" dataKey={`max__${t.capacityId}`} name={t.name} stroke={seriesColor(i)} strokeWidth={1.5} dot={false} connectNulls />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Right-sizing */}
            <Card padding="none">
              <div className="flex items-center justify-between px-4 pt-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Right-sizing (60-day telemetry)</h2>
                  {canViewCost && <CostSourceBadge source="actual" window="Current cost · 30d" />}
                  {canViewCost && <CostSourceBadge source="modeled" />}
                </div>
                {canViewCost && data.ratesAsOf && (
                  <span className="text-xs text-[var(--color-text-secondary)]">projection rates as of {data.ratesAsOf.slice(0, 10)}</span>
                )}
              </div>
              <DataTable
                columns={rightSizingColumns.filter((c) => canViewCost || (c.key !== 'currentMonthlyUsd' && c.key !== 'monthlyDeltaUsd'))}
                rows={data.rightSizing}
                rowKey={(r) => r.capacityId}
                showPagination={false}
                emptyMessage="No collectable capacities"
              />
            </Card>

            {/* Efficiency ranking + throttling */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <Card padding="none">
                <div className="flex items-center gap-2 px-4 pt-4">
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Efficiency ranking</h2>
                  {canViewCost && <CostSourceBadge source="actual" window="$/hr · 30d" />}
                  <AppTooltip title="Efficiency = (average CU% ÷ actual $/hr from real Azure billing) for each capacity, scaled 0–100 against the single most cost-efficient capacity in your fleet (100 = the best utilization-per-dollar today). A low score does NOT mean a capacity is wrong-sized — it may just cost more per hour for the same utilization; check the Right-sizing verdict for that.">
                    <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                  </AppTooltip>
                </div>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">avg CU% per $/hr, normalized to the fleet best (100 = most cost-efficient)</p>
                <DataTable
                  columns={efficiencyColumns.filter((c) => canViewCost || c.key !== 'pricePerHour')}
                  rows={data.efficiencyRanking}
                  rowKey={(r) => r.capacityId}
                  showPagination={false}
                  emptyMessage="No collectable capacities"
                />
              </Card>

              <Card>
                <h2 className="mb-1 text-base font-semibold text-[var(--color-text-primary)]">Throttling</h2>
                {data.throttlingTimeline.length === 0 ? (
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    No readings above 80% in the window — nothing throttled. 🎉
                  </p>
                ) : (
                  <>
                    <p className="mb-2 text-xs text-[var(--color-text-secondary)]">Hourly readings above 80% CU, by day — the daily pattern is often more telling than the raw list below.</p>
                    <div className="mb-3 h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={throttlingByDay.days}>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={30} allowDecimals={false} />
                          <Tooltip {...chartTooltipStyle} />
                          {throttlingByDay.capacities.map(([id, name], i) => (
                            <Bar key={id} dataKey={id} name={name} stackId="throttle" fill={seriesColor(i)} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <h3 className="mb-1 text-xs font-medium text-[var(--color-text-secondary)]">Raw readings — last 7 days</h3>
                    <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
                      {data.throttlingTimeline.slice(0, 40).map((t, i) => (
                        <li key={`${t.capacityId}-${t.hourUtc}-${i}`} className="flex items-center justify-between gap-2">
                          <span className="truncate text-[var(--color-text-primary)]">{t.capacityName}</span>
                          <span className="whitespace-nowrap text-xs text-[var(--color-text-secondary)]">
                            {formatEastern(t.hourUtc)} · {t.readingsAbove80} readings &gt;80%
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Card>
            </div>

            {/* Fingerprint — one diagram, capacity chosen from a selector */}
            <div>
              <h2 className="mb-1 text-base font-semibold text-[var(--color-text-primary)]">Usage fingerprint (weekday × hour mean CU%, 60 days)</h2>
              <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
                Average CU utilization for each hour-of-day × day-of-week combination over the last 60 days — reveals recurring load patterns (nightly batch jobs, weekday business hours, etc.) that a single daily average hides.
              </p>
              {data.fingerprints.length === 0 ? (
                <Card>
                  <NotCollected reason="No telemetry collected in the 60-day window" />
                </Card>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="w-full max-w-xs">
                      <Select
                        label="Capacity"
                        selectSize="sm"
                        value={effectiveFingerprintId ?? ''}
                        onChange={(e) => setSelectedFingerprintId(e.target.value)}
                        options={data.fingerprints.map((f) => ({ value: f.capacityId, label: f.name }))}
                      />
                    </div>
                    <HeatLegend />
                  </div>
                  {selectedFingerprint && <FingerprintCard fp={selectedFingerprint} />}
                </div>
              )}
            </div>

            {/* Distribution */}
            <Card padding="none">
              <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">What runs where</h2>
              <DataTable
                columns={[
                  {
                    key: 'name',
                    header: 'Capacity',
                    width: '26%',
                    render: (d) => (
                      <div className="min-w-0">
                        <div className="truncate font-medium text-[var(--color-text-primary)]">{d.name}</div>
                        <div className="truncate text-xs text-[var(--color-text-secondary)]">{d.subscriptionName ?? 'no subscription'}</div>
                      </div>
                    ),
                  },
                  { key: 'workspaceCount', header: 'Workspaces', width: '15%', align: 'right' },
                  { key: 'lakehouseCount', header: 'Lakehouses', width: '15%', align: 'right' },
                  { key: 'warehouseCount', header: 'Warehouses', width: '15%', align: 'right' },
                  // Spec 286 T159 item 32: the sum keeps sizes the last collection did not measure; say how many.
                  { key: 'storageGb', header: 'Storage (GB)', width: '15%', align: 'right', render: (d) => <>{d.storageGb.toLocaleString()}<UnmeasuredSizeTag count={d.sizeNotMeasuredCount} /></> },
                ] as DataTableColumn<InsightsPayload['distribution'][number]>[]}
                rows={data.distribution}
                rowKey={(d) => d.capacityId}
                pageSize={10}
                emptyMessage="No active capacities"
              />
            </Card>
          </div>
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default CapacityInsightsPage;
